import { memo } from "react";
import { Buildings, Globe, Warning } from "@phosphor-icons/react";
import { Skeleton } from "components/ui/skeleton";
import { formatCurrency } from "shared/utils/currency";
import type {
  ImportReconciliation,
  ReconciliationBucket,
} from "../hooks/useImportReconciliation";

interface CardProps {
  title: string;
  bucket: ReconciliationBucket;
  accentClassName: string;
  isLoading: boolean;
  /** Renders the card as the emphasized grand-total (filled, bolder). */
  emphasized?: boolean;
  /** Kleine toelichting onder het bedrag, bv. waarom een cijfer globaal blijft. */
  note?: string;
  compact?: boolean;
  /**
   * Het cijfer is **onbekend** i.p.v. nul: de Odoo-cijfers konden niet opgebouwd
   * worden. Toont "nog niet berekend" in plaats van € 0,00, want dat laatste leest
   * als "er is niets te importeren".
   */
  unknown?: boolean;
}

const ReconciliationCard = memo(
  ({
    title,
    bucket,
    accentClassName,
    isLoading,
    emphasized,
    note,
    compact,
    unknown,
  }: CardProps) => (
    <div
      className={`min-w-0 flex-1 rounded-md border border-l-4 px-3 py-2 ${accentClassName} ${
        emphasized ? "border-neutral-300 bg-neutral-50" : "border-neutral-200 bg-white"
      }`}
    >
      <div
        className={`text-[10px] font-semibold tracking-wider uppercase ${
          emphasized ? "text-neutral-primary" : "text-neutral-secondary-mid"
        }`}
      >
        {title}
      </div>
      {isLoading ? (
        <div className="mt-1.5 flex flex-col gap-1.5">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-3 w-24" />
        </div>
      ) : unknown ? (
        <>
          <div
            className={`text-neutral-secondary-mid mt-0.5 font-semibold ${
              compact ? "text-base" : "text-lg"
            }`}
          >
            Nog niet berekend
          </div>
          <span className="text-neutral-secondary-mid text-xs">
            De boekhoudgegevens konden niet opgehaald worden
          </span>
        </>
      ) : (
        <>
          <div className="mt-0.5 flex items-baseline gap-1.5">
            <span
              className={`text-neutral-primary font-bold tabular-nums ${
                compact ? "text-lg" : "text-xl"
              }`}
            >
              {formatCurrency(bucket.value)}
            </span>
            <span className="text-neutral-secondary-mid text-[11px] font-medium whitespace-nowrap">
              excl. btw
            </span>
          </div>
          <span className="text-neutral-secondary-mid text-xs whitespace-nowrap">
            {bucket.count} factu{bucket.count !== 1 ? "ren" : "ur"}
          </span>
          {note && (
            <span className="text-neutral-secondary-mid mt-0.5 block text-[10px] leading-tight">
              {note}
            </span>
          )}
        </>
      )}
    </div>
  ),
);
ReconciliationCard.displayName = "ReconciliationCard";

/**
 * The "+" / "=" sign shown between the cards to read them as an equation:
 * reeds geïmporteerd + te importeren + zonder kostenplaats = alle facturen.
 */
const Operator = memo(({ symbol }: { symbol: "+" | "=" }) => (
  <div
    aria-hidden="true"
    className="text-neutral-secondary-mid flex shrink-0 items-center justify-center self-center px-0.5 text-lg font-bold lg:px-1"
  >
    {symbol}
  </div>
));
Operator.displayName = "Operator";

export interface ImportReconciliationCardsProps {
  data: ImportReconciliation | null;
  isLoading: boolean;
  /** Gebouw waarop de pagina gefilterd staat, voor de kop van het selectieblok. */
  buildingId?: string;
}

const EMPTY: ReconciliationBucket = { count: 0, value: 0 };

/**
 * Vergelijking tussen wat er in de Odoo-boekhouding op de renovatierekeningen staat en
 * wat YallyHub kent. Alle bedragen excl. btw.
 *
 * **Twee blokken, bewust gescheiden:**
 *
 * 1. *Volledige boekhouding* — de rekensom
 *    `reeds geïmporteerd + te importeren + zonder kostenplaats = alles`, altijd op de
 *    **globale** cijfers. Die som klopte eerder niet: "zonder kostenplaats" hééft geen
 *    gebouw (er is geen analytische rekening), dus met een gebouwfilter actief werden twee
 *    gefilterde bedragen opgeteld bij één globaal bedrag. Nu staat de som op één scope.
 * 2. *Deze selectie* — alleen zichtbaar met een filter actief, met de cijfers die wél per
 *    gebouw te bepalen zijn. "Zonder kostenplaats" staat daar met zoveel woorden niet in.
 */
export const ImportReconciliationCards = memo(
  ({ data, isLoading, buildingId }: ImportReconciliationCardsProps) => {
    // Terugval op de gefilterde emmers wanneer `global` ontbreekt (oudere backend):
    // dan leest de rekensom zoals voorheen i.p.v. op nul te staan.
    const globaal = data?.global;
    const imported = globaal?.imported ?? data?.imported ?? EMPTY;
    const toImport = globaal?.toImport ?? data?.toImport ?? EMPTY;
    const withoutKostenplaats =
      globaal?.withoutKostenplaats ?? data?.withoutKostenplaats ?? EMPTY;

    // "Alle renovatiefacturen in boekhouding" = som van de drie globale emmers.
    const total: ReconciliationBucket = {
      count: imported.count + toImport.count + withoutKostenplaats.count,
      value: imported.value + toImport.value + withoutKostenplaats.value,
    };

    // Konden de Odoo-cijfers niet opgebouwd worden, dan zijn "te importeren" en
    // "zonder kostenplaats" onbekend — en dus ook het totaal en de rekensom.
    // Ze als € 0,00 tonen zei "er valt niets te importeren", terwijl er facturen
    // klaarstonden.
    const cijfersOnbekend = data ? data.analyticsReady === false : false;

    const heeftScope = data?.hasScope ?? false;
    // Terugval op het gebouw uit het antwoord: dan staat er nog steeds op welk gebouw
    // gefilterd is, ook als een aanroeper de prop vergeet mee te geven.
    const gefilterdGebouw = buildingId ?? data?.scopedBuildingId ?? null;
    const ledgerAccounts = data?.ledgerAccounts ?? [];

    return (
      <div className="px-6 pt-3 pb-1">
        {/* ─── Blok 1: de volledige boekhouding (rekensom) ─────────────────── */}
        <div className="mb-1.5 flex items-center gap-1.5">
          <Globe size={13} weight="duotone" className="text-neutral-secondary-mid" />
          <span className="text-neutral-primary text-xs font-semibold">
            Volledige boekhouding
          </span>
          <span className="text-neutral-secondary-mid text-[11px]">
            — alle gebouwen, filters niet meegerekend
          </span>
        </div>

        <div className="flex flex-col gap-2 lg:flex-row lg:items-stretch">
          <ReconciliationCard
            title="Waarde facturen reeds geïmporteerd"
            bucket={imported}
            accentClassName="border-l-moss-primary"
            isLoading={isLoading}
          />
          <Operator symbol="+" />
          <ReconciliationCard
            title="Waarde facturen te importeren"
            bucket={toImport}
            accentClassName="border-l-brick-primary"
            isLoading={isLoading}
            unknown={cijfersOnbekend}
          />
          <Operator symbol="+" />
          <ReconciliationCard
            title="Waarde facturen zonder kostenplaats"
            bucket={withoutKostenplaats}
            accentClassName="border-l-amber-400"
            isLoading={isLoading}
            unknown={cijfersOnbekend}
            note="Kan niet per gebouw: geen kostenplaats = geen gebouw"
          />
          <Operator symbol="=" />
          <ReconciliationCard
            title="Alle renovatiefacturen in boekhouding"
            bucket={total}
            accentClassName="border-l-neutral-700"
            isLoading={isLoading}
            unknown={cijfersOnbekend}
            emphasized
          />
        </div>

        {/* ─── Blok 2: de huidige selectie ──────────────────────────────────── */}
        {heeftScope && (
          <div className="border-moss-primary/30 bg-moss-primary/5 mt-3 rounded-md border border-dashed p-3">
            <div className="mb-1.5 flex items-center gap-1.5">
              <Buildings size={13} weight="duotone" className="text-moss-primary" />
              <span className="text-neutral-primary text-xs font-semibold">
                Deze selectie
              </span>
              {/* Het gebouw waarop gefilterd staat, i.p.v. een vage "met de actieve
                  filters": zo staat er wát er gefilterd is, niet dát er gefilterd is. */}
              <span className="text-neutral-secondary-mid text-[11px]">
                {gefilterdGebouw ? `— gebouw ${gefilterdGebouw}` : "— gefilterd"}
              </span>
            </div>

            <div className="flex flex-col gap-2 lg:flex-row lg:items-stretch">
              <ReconciliationCard
                title="Reeds geïmporteerd in deze selectie"
                bucket={data?.imported ?? EMPTY}
                accentClassName="border-l-moss-primary"
                isLoading={isLoading}
                compact
              />
              <ReconciliationCard
                title="Te importeren in deze selectie"
                bucket={data?.toImport ?? EMPTY}
                accentClassName="border-l-brick-primary"
                isLoading={isLoading}
                unknown={cijfersOnbekend}
                compact
              />
            </div>

            <p className="text-neutral-secondary-mid mt-1.5 flex items-start gap-1 text-[11px]">
              <Warning
                size={12}
                weight="fill"
                className="mt-0.5 shrink-0 text-amber-600"
              />
              <span>
                Deze twee tellen niet op tot een totaal: facturen{" "}
                <strong>zonder kostenplaats</strong> horen bij geen enkel gebouw, dus die
                staan alleen in de volledige boekhouding hierboven.
              </span>
            </p>
          </div>
        )}

        {ledgerAccounts.length > 0 && (
          <p className="text-neutral-secondary-mid mt-1.5 text-[11px]">
            <span className="font-semibold">Uitgelezen grootboekrekeningen:</span>{" "}
            {ledgerAccounts.join(" · ")}
          </p>
        )}
        <p className="text-neutral-secondary-mid mt-0.5 text-[11px]">
          Alle bedragen excl. btw · Vergelijking met Odoo-boekhouding
          {data?.lastUpdated ? ` · laatst bijgewerkt ${data.lastUpdated}` : ""}
        </p>
      </div>
    );
  },
);
ImportReconciliationCards.displayName = "ImportReconciliationCards";
